import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import BaseButton from "@reusables/BaseButton";
import BaseInput from "@reusables/BaseInput";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BasePhoneInput from "@reusables/BasePhoneInput";
import BaseModal from "@reusables/Modals/BaseModal";
import { toastError } from "@helpers/utils";
import { Supplier } from "@/types/general";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";
import { useCreateSupplierContactMutation } from "@redux/features/suppliers/suppliersApi";

type CreateTheirReferenceModalProps = {
    supplier?: Supplier.Extended;
    isOpen: boolean;
    onClose: () => void;
    onCreate: (contact: { id: number, name: string, phone?: string, email?: string }) => void;
}

const formScheme = z.object({
    name: z.string().min(2),
    phone: z.string().optional(),
    email: z.string().email().optional()
});

type FormTyping = z.infer<typeof formScheme>

export default function CreateTheirReferenceModal({
                                                      supplier,
                                                      isOpen,
                                                      onClose,
                                                      onCreate
                                                  }: CreateTheirReferenceModalProps) {
    const { t } = useTranslation("", { keyPrefix: "purchaseOrder.general.modals.createTheirReference" });

    const [createContact, { isLoading: isCreationLoading }] = useCreateSupplierContactMutation();

    const { control, handleSubmit, reset } = useForm<FormTyping>({
        resolver: zodResolver(formScheme)
    });

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen]);

    const onSubmit = handleSubmit((data) => {
        if (!supplier) {
            toast.error("Supplier is not selected");
            return;
        }

        createContact({
            supplier_id: supplier?.id,
            name: data.name as string,
            phone: data.phone as string,
            email: data.email as string
        }).unwrap()
            .then((data) => {
                onCreate(data);
            })
            .catch((error) => {
                toastError(error);
            });
    });

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            width={562}
            padding="56px"
            useCloseIcon
            isLoading={isCreationLoading}
        >
            <form className="space-y-8" onSubmit={onSubmit}>
                <div className="modal-title">{t("heading")}</div>

                <BaseInputsGrid cols={1}>
                    <Controller
                        name="name"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.name")}
                            />
                        )}
                    />

                    <Controller
                        name="phone"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BasePhoneInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.phone")}
                                variant="custom"
                            />
                        )}
                    />

                    <Controller
                        name="email"
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.email")}
                            />
                        )}
                    />
                </BaseInputsGrid>

                <BaseButton
                    buttonWidth="100%"
                    text={t("buttons.create")}
                    size="md"
                    type={"submit"}
                />

            </form>
        </BaseModal>
    );
}
